import React from 'react'
import { Typography, TypographyProps } from '@mui/material'
import { SxProps, Theme } from '@mui/material/styles'

interface TruncatedTextProps extends Omit<TypographyProps, 'children'> {
  text: string
  lines?: number
  maxChars?: number
  children?: never // Prevent using children prop
}

/**
 * TruncatedText component for displaying text with ellipsis
 * Can truncate by number of lines or by max characters
 */
const TruncatedText: React.FC<TruncatedTextProps> = ({ text, lines = 1, maxChars, sx, ...props }) => {
  // If maxChars is set, truncate the text
  const truncatedText = maxChars && text.length > maxChars ? `${text.substring(0, maxChars)}...` : text

  // Style for multi-line truncation
  const multiLineTruncationStyle: SxProps<Theme> =
    lines > 1
      ? {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: lines,
          WebkitBoxOrient: 'vertical',
        }
      : {}

  // Style for single-line truncation
  const singleLineTruncationStyle: SxProps<Theme> =
    lines === 1 && !maxChars
      ? {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }
      : {}

  return (
    <Typography
      sx={{
        ...(singleLineTruncationStyle as object),
        ...(multiLineTruncationStyle as object),
        ...(sx as object),
      }}
      {...props}
    >
      {truncatedText}
    </Typography>
  )
}

export default TruncatedText
